import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getBillingBalance,
  getTransactions,
  getTransactionById,
  addFunds,
  processPayment,
  getPaymentMethods,
  addPaymentMethod,
  removePaymentMethod,
  setDefaultPaymentMethod,
  getBillingSummary,
  exportTransactionsCSV,
  requestRefund,
  getCreditPackages,
  purchaseCredits,
  getAutoReplenishSettings,
  updateAutoReplenishSettings,
} from '@/services/billing.service';
import { queryKeys } from '@/lib/queryClient';
import type {
  TransactionListRequest,
  PaymentRequest,
  PurchaseCreditRequest,
  AutoReplenishUpdateRequest,
} from '@/types/api.types';

/**
 * Hook to get billing balance
 */
export const useBillingBalance = (enabled = true) => {
  return useQuery({
    queryKey: queryKeys.billing.balance,
    queryFn: getBillingBalance,
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to get transaction history
 */
export const useTransactions = (params?: TransactionListRequest) => {
  return useQuery({
    queryKey: queryKeys.billing.transactions(params),
    queryFn: () => getTransactions(params),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

/**
 * Hook to get a specific transaction
 */
export const useTransaction = (transactionId: string, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.billing.transaction(transactionId),
    queryFn: () => getTransactionById(transactionId),
    enabled: enabled && !!transactionId,
  });
};

/**
 * Hook to add funds to account
 */
export const useAddFunds = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentData: PaymentRequest) => addFunds(paymentData),
    onSuccess: () => {
      // Invalidate balance and transactions to refresh
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.balance });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.transactions() });
    },
    onError: (error) => {
      console.error('Failed to add funds:', error);
    },
  });
};

/**
 * Hook to process a payment
 */
export const useProcessPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentData: PaymentRequest) => processPayment(paymentData),
    onSuccess: () => {
      // Invalidate balance and transactions to refresh
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.balance });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.transactions() });
    },
    onError: (error) => {
      console.error('Payment processing failed:', error);
    },
  });
};

/**
 * Hook to get payment methods
 */
export const usePaymentMethods = () => {
  return useQuery({
    queryKey: ['billing', 'payment-methods'],
    queryFn: getPaymentMethods,
  });
};

/**
 * Hook to add a payment method
 */
export const useAddPaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentMethodId: string) => addPaymentMethod(paymentMethodId),
    onSuccess: () => {
      // Invalidate payment methods to refresh
      queryClient.invalidateQueries({ queryKey: ['billing', 'payment-methods'] });
    },
    onError: (error) => {
      console.error('Failed to add payment method:', error);
    },
  });
};

/**
 * Hook to remove a payment method
 */
export const useRemovePaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentMethodId: string) => removePaymentMethod(paymentMethodId),
    onSuccess: () => {
      // Invalidate payment methods to refresh
      queryClient.invalidateQueries({ queryKey: ['billing', 'payment-methods'] });
    },
    onError: (error) => {
      console.error('Failed to remove payment method:', error);
    },
  });
};

/**
 * Hook to set default payment method
 */
export const useSetDefaultPaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentMethodId: string) => setDefaultPaymentMethod(paymentMethodId),
    onSuccess: () => {
      // Invalidate payment methods to refresh
      queryClient.invalidateQueries({ queryKey: ['billing', 'payment-methods'] });
    },
    onError: (error) => {
      console.error('Failed to set default payment method:', error);
    },
  });
};

/**
 * Hook to get billing summary
 */
export const useBillingSummary = (startDate: string, endDate: string, enabled = true) => {
  return useQuery({
    queryKey: ['billing', 'summary', startDate, endDate],
    queryFn: () => getBillingSummary(startDate, endDate),
    enabled: enabled && !!startDate && !!endDate,
  });
};

/**
 * Hook to export transactions as CSV
 */
export const useExportTransactionsCSV = () => {
  return useMutation({
    mutationFn: (params?: TransactionListRequest) => exportTransactionsCSV(params),
    onSuccess: (blob) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `transactions-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => {
      console.error('Failed to export transactions:', error);
    },
  });
};

/**
 * Hook to request a refund
 */
export const useRequestRefund = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transactionId, reason }: { transactionId: string; reason: string }) =>
      requestRefund(transactionId, reason),
    onSuccess: () => {
      // Invalidate transactions to refresh
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.transactions() });
    },
    onError: (error) => {
      console.error('Failed to request refund:', error);
    },
  });
};

/**
 * Hook to get available credit packages
 */
export const useCreditPackages = () => {
  return useQuery({
    queryKey: ['billing', 'credit-packages'],
    queryFn: getCreditPackages,
    staleTime: 1000 * 60 * 30, // 30 minutes - packages rarely change
  });
};

/**
 * Hook to purchase credits
 */
export const usePurchaseCredits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PurchaseCreditRequest) => purchaseCredits(data),
    onSuccess: () => {
      // Invalidate balance and transactions to refresh
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.balance });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.transactions() });
    },
    onError: (error) => {
      console.error('Failed to purchase credits:', error);
    },
  });
};

/**
 * Hook to get auto-replenish settings (Phase 2)
 */
export const useAutoReplenishSettings = (enabled = true) => {
  return useQuery({
    queryKey: ['billing', 'auto-replenish'],
    queryFn: getAutoReplenishSettings,
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to update auto-replenish settings (Phase 2)
 */
export const useUpdateAutoReplenish = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AutoReplenishUpdateRequest) => updateAutoReplenishSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'auto-replenish'] });
    },
    onError: (error) => {
      console.error('Failed to update auto-replenish settings:', error);
    },
  });
};
